import { h, render } from 'https://esm.sh/preact';
import htm from 'https://esm.sh/htm';
import { useState, useEffect } from 'https://esm.sh/preact/hooks';

const html = htm.bind(h);

// Icons
const UploadIcon = () => html`
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
`;

const TrashIcon = () => html`
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
`;

const CopyIcon = () => html`
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
`;

const ShieldCheckIcon = ({ width = 16, height = 16, style = {} }) => html`
  <svg width=${width} height=${height} viewBox="0 0 16 16" fill="none" style=${style} xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2.25H3C2.66848 2.25 2.35054 2.3817 2.11612 2.61612C1.8817 2.85054 1.75 3.16848 1.75 3.5V7C1.75 10.3931 3.395 12.4513 4.775 13.5806C6.25688 14.7925 7.74 15.2056 7.8025 15.2238C7.93184 15.2587 8.06816 15.2587 8.1975 15.2238C8.26 15.2063 9.74313 14.7925 11.225 13.5806C12.605 12.4513 14.25 10.3931 14.25 7V3.5C14.25 3.16848 14.1183 2.85054 13.8839 2.61612C13.6495 2.3817 13.3315 2.25 13 2.25Z" fill="currentColor" />
  </svg>
`;

const PriceEstimatorApp = () => {
  const config = window.PriceEstimatorConfig || {};
  config.assets = config.assets || {};

  const getLocale = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('locale')) return urlParams.get('locale').toLowerCase();
    return (config.locale || '').toLowerCase();
  };
  const locale = getLocale().substring(0, 2);
  const isNO = ['no', 'nb'].includes(locale);
  const isDE = locale === 'de';

  const t = (noText, deText, enText) => {
    if (isNO) return noText;
    if (isDE) return deText;
    return enText;
  };

  // State
  const [step, setStep] = useState('welcome'); // welcome, upload, processing, detected, confirm, overview, calculating, estimate
  const [calcStage, setCalcStage] = useState(0);
  const [windows, setWindows] = useState([]);
  const [draftWindow, setDraftWindow] = useState(null);
  
  const [uploadError, setUploadError] = useState('');
  const [processingMsg, setProcessingMsg] = useState('');
  
  // Multi-window queue state
  const [detectedWindows, setDetectedWindows] = useState([]);
  const [currentDetectionIdx, setCurrentDetectionIdx] = useState(0);
  
  // Suggested form fields on confirm step
  const [estWidth, setEstWidth] = useState(0);
  const [estHeight, setEstHeight] = useState(0);
  const [estFrameColor, setEstFrameColor] = useState('anthracite');
  const [estMotor, setEstMotor] = useState('solar');
  const [estAccuracy, setEstAccuracy] = useState('rough_estimate');
  const [windowName, setWindowName] = useState('');

  // Load from local storage on mount
  useEffect(() => {
    const saved = localStorage.getItem('screenlux_measurements');
    const isEst = localStorage.getItem('screenlux_estimate_transfer') === 'true';
    if (saved && isEst) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setWindows(parsed);
          setStep('overview');
        }
      } catch (e) {
        console.error('Failed to parse saved estimates', e);
      }
    }
  }, []);

  // Calculating timer for estimate
  useEffect(() => {
    if (step === 'calculating') {
      setCalcStage(0);
      const t1 = setTimeout(() => setCalcStage(1), 1000);
      const t2 = setTimeout(() => setCalcStage(2), 2000);
      const t3 = setTimeout(() => setCalcStage(3), 3000);
      const tFinish = setTimeout(() => {
        setStep('estimate');
      }, 4000);

      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        clearTimeout(tFinish);
      };
    }
  }, [step]);

  const getScreenEstimatePrice = (width, height, motor, frameColor) => {
    try {
      if (!window.ScreenluxEngine || !window.ScreenluxData || !window.ScreenluxData.screens || window.ScreenluxData.screens.length === 0) {
        throw new Error('Pricing engine/data not initialized');
      }
      const val = window.ScreenluxEngine.validateDimensions(width, height);
      if (!val.valid) return 0;

      // Apply Wall mounted offsets to estimate dimensions matching configurator V2
      const finalWidth = width + 76;
      const heightWithSlim = height + 85 + 30; // 85 mm cassette + 30 mm bottom rail
      const finalHeight = window.ScreenluxEngine.cassetteFor(finalWidth, heightWithSlim) === 'large'
        ? (height + 120 + 45) // large cassette 
        : heightWithSlim; // slim cassette

      const config = {
        width: finalWidth,
        height: finalHeight,
        motor: motor || 'solar',
        frameColor: frameColor || 'anthracite',
        fabricType: '5-percent',
        fabricColor: 'graphite',
        cassetteSize: window.ScreenluxEngine.cassetteFor(finalWidth, finalHeight),
        mountingBracket: 'none'
      };
      const raw = window.ScreenluxEngine.calculateScreenPrice(config, window.ScreenluxData.config);
      const variant = window.ScreenluxEngine.matchVariant(raw, window.ScreenluxData.screens);
      return variant ? variant.price : raw;
    } catch (e) {
      console.warn('Fallback estimation price used:', e);
      if (width && height && (width < 650 || height < 200 || width > 5000 || height > 3000)) {
        return 0;
      }
      const isNOK = window.PriceEstimatorConfig?.locale?.toLowerCase().includes('no') || window.PriceEstimatorConfig?.locale?.toLowerCase().includes('nb');
      const base = isNOK ? (motor === 'solar' ? 9500 : 7500) : (motor === 'solar' ? 750 : 600);
      return base * 100;
    }
  };

  const getPlacementName = (placement, index, total) => {
    switch (placement) {
      case 'top_left':
        return t('Vindu oppe til venstre', 'Fenster oben links', 'Window top left');
      case 'top_center':
        return t('Vindu oppe i midten', 'Fenster oben Mitte', 'Window top center');
      case 'top_right':
        return t('Vindu oppe til høyre', 'Fenster oben rechts', 'Window top right');
      case 'middle_left':
        return t('Vindu i midten til venstre', 'Fenster Mitte links', 'Window middle left');
      case 'middle_center':
        return t('Vindu i midten', 'Fenster Mitte', 'Window middle center');
      case 'middle_right':
        return t('Vindu i midten til høyre', 'Fenster Mitte rechts', 'Window middle right');
      case 'bottom_left':
        return t('Vindu nede til venstre', 'Fenster unten links', 'Window bottom left');
      case 'bottom_center':
        return t('Vindu nede i midten', 'Fenster unten Mitte', 'Window bottom center');
      case 'bottom_right':
        return t('Vindu nede til høyre', 'Fenster unten rechts', 'Window bottom right');
      case 'single_window':
      default:
        return t(`Vindu ${windows.length + 1 + index}`, `Fenster ${windows.length + 1 + index}`, `Window ${windows.length + 1 + index}`);
    }
  };

  const handleStart = () => {
    setStep('upload');
  };

  const startNewWindow = () => {
    setDraftWindow({
      id: Date.now().toString(),
      image: '',
      width: 0,
      height: 0,
      name: '',
      suggestedFrameColor: 'anthracite',
      suggestedMotor: 'solar',
      accuracy: 'rough_estimate',
      isEstimate: true
    });
    setDetectedWindows([]);
    setCurrentDetectionIdx(0);
    setUploadError('');
    setStep('upload');
  };

  const handleRetake = () => {
    setDetectedWindows([]);
    setCurrentDetectionIdx(0);
    setStep('upload');
  };

  // Compress image helper
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const max_size = 1200;

          if (width > height) {
            if (width > max_size) {
              height *= max_size / width;
              width = max_size;
            }
          } else {
            if (height > max_size) {
              width *= max_size / height;
              height = max_size;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // We use JPEG compression to ensure rapid upload
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(dataUrl);
        };
        img.onerror = () => reject(new Error('Failed to load image object'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setStep('processing');
    setProcessingMsg(t('Komprimerer bilde...', 'Bild wird komprimiert...', 'Compressing image...'));

    try {
      const compressedBase64 = await compressImage(file);
      setDraftWindow(prev => ({ ...prev, image: compressedBase64 }));
      await performEstimation(compressedBase64);
    } catch (err) {
      console.error(err);
      setUploadError(t('Klarte ikke å behandle bildet. Prøv igjen.', 'Bildverarbeitung fehlgeschlagen. Bitte erneut versuchen.', 'Failed to process image. Please try again.'));
      setStep('upload');
    }
  };

  const performEstimation = async (base64Image) => {
    const statuses = [
      t('Uploader bilde til AI...', 'Bild wird an die KI übertragen...', 'Uploading photo to AI...'),
      t('Søker etter A4-referanseark på glasset...', 'Suche nach A4-Referenzblatt auf der Scheibe...', 'Searching for taped A4 sheet on the glass...'),
      t('Beregner bredde og høyde i millimeter...', 'Breite und Höhe werden in Millimetern berechnet...', 'Calculating width and height in millimeters...'),
      t('Analyserer fasadefarge for rammefinish...', 'Fassadenfarbe wird für Rahmenfarbe ermittelt...', 'Analyzing facade color for frame finish...')
    ];

    let currentStatusIdx = 0;
    setProcessingMsg(statuses[0]);

    const interval = setInterval(() => {
      currentStatusIdx = (currentStatusIdx + 1) % statuses.length;
      setProcessingMsg(statuses[currentStatusIdx]);
    }, 2500);

    try {
      const res = await fetch('/apps/measurements?action=estimate-dimensions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: base64Image })
      });

      clearInterval(interval);

      if (!res.ok) {
        throw new Error('Endpoint returned error status');
      }

      const data = await res.json();
      const detected = data.windows || [];

      if (detected.length === 0) {
        throw new Error(t(
          'Ingen vinduer ble funnet på bildet. Vennligst prøv igjen med et annet bilde.',
          'Keine Fenster auf dem Foto erkannt. Bitte versuche es mit einem anderen Bild.',
          'No windows detected in the photo. Please try again with another image.'
        ));
      }

      setDetectedWindows(detected);
      setCurrentDetectionIdx(0);

      // Seed first detected window
      const firstWin = detected[0];
      setEstWidth(firstWin.width || 1200);
      setEstHeight(firstWin.height || 1800);
      setEstFrameColor(firstWin.suggestedFrameColor || 'anthracite');
      setEstMotor('solar'); // Recommend solar by default
      setEstAccuracy(firstWin.accuracy || 'rough_estimate');
      setWindowName(getPlacementName(firstWin.placement, 0, detected.length));

      if (detected.length > 1) {
        setStep('detected');
      } else {
        setStep('confirm');
      }
    } catch (err) {
      console.error(err);
      clearInterval(interval);
      setUploadError(err.message || t('AI-analysen feilet. Vennligst prøv igjen.', 'KI-Analyse fehlgeschlagen. Bitte versuche es erneut.', 'AI analysis failed. Please try again.'));
      setStep('upload');
    }
  };

  const handleConfirmWindow = () => {
    const windowId = Date.now().toString() + '-' + Math.random().toString(36).substr(2, 5);

    const confirmedWindow = {
      id: windowId,
      image: draftWindow ? draftWindow.image : '',
      widthWidth: estWidth, // Map to wizard's parameter format
      widthTop: estWidth,
      widthMiddle: estWidth,
      widthBottom: estWidth,
      heightHeight: estHeight,
      heightLeft: estHeight,
      heightMiddle: estHeight,
      heightRight: estHeight,
      name: windowName,
      suggestedFrameColor: estFrameColor,
      suggestedMotor: estMotor, // Store motor selection per window
      accuracy: estAccuracy,
      mountType: 'Wall mounted', // Default to wall mounting for estimation safety
      type: 'Window without waterboard on top',
      measurementGuarantee: false,
      isEstimate: true
    };

    const newWindows = [...windows, confirmedWindow];
    setWindows(newWindows);
    
    // Save to local storage
    localStorage.setItem('screenlux_measurements', JSON.stringify(newWindows));
    localStorage.setItem('screenlux_estimate_transfer', 'true');

    // Check if there are more windows in the queue
    if (currentDetectionIdx < detectedWindows.length - 1) {
      const nextIdx = currentDetectionIdx + 1;
      setCurrentDetectionIdx(nextIdx);

      const nextWin = detectedWindows[nextIdx];
      setEstWidth(nextWin.width || 1200);
      setEstHeight(nextWin.height || 1800);
      setEstFrameColor(nextWin.suggestedFrameColor || 'anthracite');
      setEstMotor('solar'); // Recommend solar by default
      setEstAccuracy(nextWin.accuracy || 'rough_estimate');
      setWindowName(getPlacementName(nextWin.placement, nextIdx, detectedWindows.length));
    } else {
      setStep('overview');
    }
  };

  const deleteWindow = (id) => {
    const filtered = windows.filter(w => w.id !== id);
    setWindows(filtered);
    localStorage.setItem('screenlux_measurements', JSON.stringify(filtered));
    if (filtered.length === 0) {
      localStorage.removeItem('screenlux_estimate_transfer');
      setStep('upload');
    }
  };

  const duplicateWindow = (win) => {
    const copySuffix = t(' (Kopi)', ' (Kopie)', ' (Copy)');
    const cleanName = win.name.endsWith(copySuffix) ? win.name.slice(0, -copySuffix.length) : win.name;
    const duplicated = {
      ...win,
      id: Date.now().toString(),
      name: cleanName + copySuffix
    };
    const newWindows = [...windows, duplicated];
    setWindows(newWindows);
    localStorage.setItem('screenlux_measurements', JSON.stringify(newWindows));
  };


  // Renders
  const renderWelcome = () => html`
    <div class="measure-content welcome-card" style="text-align: left;">
      <h1 class="measure-title" style="margin-top: 0 !important; margin-bottom: 12px; font-weight: 700; font-size: 28px;">
        ${t('Få et uforpliktende prisoverslag med AI', 'Unverbindlicher KI-Kostenvoranschlag', 'Get an Instant AI Price Estimate')}
      </h1>
      <p style="font-size: 15px; line-height: 1.6; color: var(--sl-text-secondary); margin-bottom: 24px;">
        ${t(
          'Last opp et bilde av vinduet ditt. Vår smarte AI beregner størrelsen og foreslår matchende farger, slik at du umiddelbart får se prisen i vår produktkonfigurator.',
          'Lade ein Foto deines Fensters hoch. Unsere smarte KI berechnet die Maße und schlägt passende Farben vor, sodass du sofort den Preis in unserem Konfigurator siehst.',
          'Upload a photo of your window. Our smart AI calculates the sizes and suggests matching frame colors, letting you see the price immediately in the configurator.'
        )}
      </p>

      <div class="info-card-tip" style="background-color: #F5F6FF !important; border: 1px solid #E0E7FF !important; padding: 20px; border-radius: 8px; margin-bottom: 30px; display: flex; gap: 16px;">
        <img src="${config.assets.toolMeasuringTape}" style="width: 80px; height: 80px; object-fit: cover; border-radius: 6px; flex-shrink: 0;" />
        <div>
          <div class="info-card-header" style="margin-bottom: 6px;">
            <h4 class="info-card-title" style="margin: 0; font-weight: 600; color: var(--sl-text-primary); font-size: 15px;">${t('Tips for optimal nøyaktighet', 'Tipp für maximale Genauigkeit', 'Tip for best accuracy')}</h4>
          </div>
          <p class="info-card-message" style="margin: 0; font-size: 13.5px; color: var(--sl-text-secondary); line-height: 1.5;">
            ${t(
              'Fest et standard A4-ark flatt på vindusglasset med tape før du tar bildet. Da kan AI bruke det som referanseskala for millimeterpresisjon.',
              'Klebe vor dem Fotografieren ein normales A4-Blatt flach auf die Fensterscheibe. Die KI nutzt dies als Referenz für millimetergenaue Ergebnisse.',
              'Tape a standard A4 sheet of paper flat on the window glass before taking the photo. The AI uses this as a scaling reference for millimeter precision.'
            )}
          </p>
        </div>
      </div>

      <button class="button--brand" style="width: 100%;" onClick=${handleStart}>
        ${t('Start prisoverslag', 'Jetzt starten', 'Start Price Estimate')}
      </button>
    </div>
  `;

  const renderUpload = () => html`
    <div>
      <div class="measure-header">
        <h1 style="margin: 0;">${t('Last opp bilde av vinduet', 'Fensterfoto hochladen', 'Upload Window Photo')}</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px; line-height: 1.5;">
          ${t(
            'Ta bilde rett forfra, og sørg for at hele vinduet (og A4-arket om du bruker det) er fullt synlig i rammen.',
            'Fotografiere das Fenster frontal und stelle sicher, dass das gesamte Fenster (und das A4-Blatt, falls verwendet) vollständig sichtbar ist.',
            'Take the photo head-on, ensuring the entire window frame (and the A4 sheet if used) is fully visible.'
          )}
        </p>
      </div>

      <div class="measure-content">
        ${uploadError && html`
          <div style="background-color: #FEF2F2; border: 1px solid #FCA5A5; color: #991B1B; padding: 12px; border-radius: 6px; font-size: 14px; margin-bottom: 16px;">
            ${uploadError}
          </div>
        `}

        <label class="upload-dropzone" style="display: flex; flex-direction: column; align-items: center; justify-content: center; border: 2px dashed #D1D5DB; border-radius: 12px; padding: 48px 24px; text-align: center; cursor: pointer; background: #FAFAFA; transition: border-color 0.2s;">
          <input type="file" accept="image/*" style="display: none;" onChange=${handleImageUpload} />
          <span style="color: #6B7280; margin-bottom: 12px;"><${UploadIcon} /></span>
          <span style="font-weight: 600; font-size: 15px; color: #111827;">${t('Ta bilde eller velg fil', 'Foto aufnehmen oder Datei wählen', 'Take photo or choose file')}</span>
          <span style="font-size: 13px; color: #9CA3AF; margin-top: 4px;">PNG, JPG eller WEBP opptil 10MB</span>
        </label>
      </div>

      ${windows.length > 0 && html`
        <div class="measure-footer measure-footer-buttons">
          <button class="button--brand-secondary" style="width: 100%;" onClick=${() => setStep('overview')}>
            ${t('Avbryt', 'Abbrechen', 'Cancel')}
          </button>
        </div>
      `}
    </div>
  `;

  const renderProcessing = () => html`
    <div class="measure-content" style="text-align: left; padding: 40px 20px;">
      <h2 style="font-size: 20px; font-weight: 700; margin-bottom: 24px; text-align: center;">
        ${t('Behandler med AI...', 'Wird von KI verarbeitet...', 'Processing with AI...')}
      </h2>

      <!-- Scanning animation card -->
      <div style="position: relative; width: 100%; max-width: 320px; height: 200px; background-color: #E5E7EB; border-radius: 12px; overflow: hidden; margin: 0 auto 30px; display: flex; align-items: center; justify-content: center; border: 1px solid rgba(0,0,0,0.06);">
        ${draftWindow && draftWindow.image && html`
          <img src=${draftWindow.image} style="width: 100%; height: 100%; object-fit: cover; opacity: 0.65;" />
        `}
        <div class="scan-line" style="position: absolute; left: 0; width: 100%; height: 4px; background: linear-gradient(180deg, rgba(79,70,229,0) 0%, #4F46E5 50%, rgba(79,70,229,0) 100%); box-shadow: 0 0 12px #4F46E5; animation: scanAnimation 2s infinite ease-in-out;"></div>
      </div>

      <div style="display: flex; align-items: center; justify-content: center; gap: 12px; min-height: 48px;">
        <span class="spinner" style="width: 20px; height: 20px; border: 2px solid #4F46E5; border-top-color: transparent; border-radius: 50%; display: inline-block; animation: spin 1s infinite linear;"></span>
        <span style="font-size: 14.5px; color: var(--sl-text-primary); font-weight: 500;">
          ${processingMsg}
        </span>
      </div>
    </div>
  `;

  const renderConfirm = () => html`
    <div>
      <div class="measure-header">
        <h1 style="margin: 0;">${t('Bekreft estimat', 'Messergebnisse bestätigen', 'Confirm AI Estimates')}</h1>
        ${detectedWindows.length > 1 && html`
          <p style="color: #4F46E5; margin-top: 6px; font-weight: 600; font-size: 14.5px; margin-bottom: 0;">
            ${t(
              `Vindu ${currentDetectionIdx + 1} av ${detectedWindows.length}`,
              `Fenster ${currentDetectionIdx + 1} von ${detectedWindows.length}`,
              `Window ${currentDetectionIdx + 1} of ${detectedWindows.length}`
            )}
          </p>
        `}
      </div>
      
      <div class="measure-content">
        <div style="display: flex; gap: 16px; margin-bottom: 20px; align-items: flex-start; flex-wrap: wrap;">
          <!-- Uploaded image preview -->
          <div style="width: 100px !important; height: 100px !important; border-radius: 8px; overflow: hidden !important; border: 1px solid rgba(0,0,0,0.1); background-color: #FAFAFA; flex-shrink: 0;">
            <img src=${draftWindow ? draftWindow.image : ''} style="width: 100% !important; height: 100% !important; object-fit: cover !important;" />
          </div>
          
          <div style="flex: 1; min-width: 200px;">
            <!-- Accuracy Badge -->
            <div style="display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: 600; margin-bottom: 8px;
              background-color: ${estAccuracy === 'a4_reference' ? '#D1FAE5' : '#FEF3C7'}; 
              color: ${estAccuracy === 'a4_reference' ? '#065F46' : '#92400E'};">
              ${estAccuracy === 'a4_reference' ? html`<${ShieldCheckIcon} width="12" height="12" />` : ''}
              <span>
                ${estAccuracy === 'a4_reference' 
                  ? t('Referanseskala funnet (høy nøyaktighet)', 'Referenzskala gefunden (hohe Genauigkeit)', 'A4 reference found (high accuracy)') 
                  : t('Grovt prisoverslag', 'Grobes Preis-Schätzung', 'Rough price estimate')}
              </span>
            </div>
            
            <div style="font-size: 13.5px; color: var(--sl-text-secondary); line-height: 1.5;">
              ${t(
                'Gå gjennom og juster målene dersom du kjenner de faktiske størrelsene. AI har også foreslått en rammeavslutning.',
                'Überprüfe die Maße und passe sie an, falls du die echten Maße kennst. Die KI hat zudem eine Rahmenfarbe vorgeschlagen.',
                'Verify the sizes and edit if you know the exact dimensions. The AI has also suggested a matching frame finish.'
              )}
            </div>
          </div>
        </div>

        <div style="display: flex; flex-direction: column; gap: 16px;">
          <!-- Window Name -->
          <div>
            <label class="field-label" style="display: block; font-size: 14px; font-weight: 600; margin-bottom: 6px; color: var(--sl-text-primary);">
              ${t('Navn på vindu', 'Fenstername', 'Window name')}
            </label>
            <input class="sl-input" type="text" value=${windowName} onChange=${(e) => setWindowName(e.target.value)} placeholder="F.eks. Stue" />
          </div>

          <div style="display: flex; gap: 12px;">
            <!-- Width Input -->
            <div style="flex: 1;">
              <label class="field-label" style="display: block; font-size: 14px; font-weight: 600; margin-bottom: 6px; color: var(--sl-text-primary);">
                ${t('Bredde (mm)', 'Breite (mm)', 'Width (mm)')}
              </label>
              <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" value=${estWidth} onChange=${(e) => setEstWidth(parseInt(e.target.value) || 0)} />
            </div>

            <!-- Height Input -->
            <div style="flex: 1;">
              <label class="field-label" style="display: block; font-size: 14px; font-weight: 600; margin-bottom: 6px; color: var(--sl-text-primary);">
                ${t('Høyde (mm)', 'Höhe (mm)', 'Height (mm)')}
              </label>
              <input class="sl-input" type="number" inputmode="numeric" pattern="[0-9]*" value=${estHeight} onChange=${(e) => setEstHeight(parseInt(e.target.value) || 0)} />
            </div>
          </div>

          ${(() => {
            const minW = 650;
            const minH = 200;
            const maxW = 5000;
            const maxH = 3000;
            if (estWidth && estHeight && (estWidth < minW || estWidth > maxW || estHeight < minH || estHeight > maxH)) {
              return html`
                <div style="color: #DC2626; font-size: 13px; font-weight: 500; margin-top: -8px;">
                  ⚠️ ${t(
                    `Mål er utenfor grensene (min. ${minW}x${minH} mm, maks. ${maxW}x${maxH} mm)`,
                    `Maße außerhalb der Grenzen (min. ${minW}x${minH} mm, max. ${maxW}x${maxH} mm)`,
                    `Dimensions out of bounds (min. ${minW}x${minH} mm, max. ${maxW}x${maxH} mm)`
                  )}
                </div>
              `;
            }
            return null;
          })()}

          <!-- Frame color suggestion -->
          <div>
            <label class="field-label" style="display: block; font-size: 14px; font-weight: 600; margin-bottom: 8px; color: var(--sl-text-primary);">
              ${t('Foreslått profilfarge (basert på fasade)', 'Empfohlene Rahmenfarbe (nach Fassade)', 'Suggested frame color (matching facade)')}
            </label>
            
            <div class="selection-grid">
              ${[
                { id: 'anthracite', title: t('Antrasitt', 'Anthrazit', 'Anthracite'), hex: '#373F47' },
                { id: 'white', title: t('Hvit', 'Weiß', 'White'), hex: '#FFFFFF', border: '1px solid #D1D5DB' },
                { id: 'black', title: t('Sort', 'Schwarz', 'Black'), hex: '#1A1A1A' }
              ].map(col => html`
                <label class="selection-card ${estFrameColor === col.id ? 'selected' : ''}" style="min-height: auto; padding: 12px;" onClick=${() => setEstFrameColor(col.id)}>
                  <input type="radio" name="estFrameColor" value=${col.id} checked=${estFrameColor === col.id} class="hidden-input" />
                  <div class="card-visual" style="margin-bottom: 4px;">
                    <span class="color-dot" style="width: 32px; height: 32px; background: ${col.hex}; border: ${col.border || 'none'};"></span>
                  </div>
                  <div class="card-text-wrapper" style="text-align: center;">
                    <span class="card-title" style="font-size: 13px;">${col.title}</span>
                  </div>
                </label>
              `)}
            </div>
          </div>

          <!-- Motor Surcharge Option Selection -->
          <div>
            <label class="field-label" style="display: block; font-size: 14px; font-weight: 600; margin-bottom: 8px; color: var(--sl-text-primary);">
              ${t('Antriebstyp', 'Antriebstyp', 'Motor option')}
            </label>
            
            <div class="selection-grid">
              ${(() => {
                const currencySymbol = window.ScreenluxData?.config?.currencySymbol || '€';
                const pSolar = getScreenEstimatePrice(estWidth, estHeight, 'solar', estFrameColor);
                const pWired = getScreenEstimatePrice(estWidth, estHeight, 'wired', estFrameColor);
                const diffCents = pSolar - pWired;
                const formattedDiff = diffCents > 0 ? `+${(diffCents / 100).toFixed(0)} ${currencySymbol}` : '';

                return [
                  { 
                    id: 'solar', 
                    title: t('Solcelle (anbefalt)', 'Solar (empfohlen)', 'Solar (recommended)'),
                    sub: formattedDiff
                  },
                  { 
                    id: 'wired', 
                    title: t('Standard kablet', 'Kabelgebunden', 'Wired motor'),
                    sub: t('Basispris', 'Basispreis', 'Base price')
                  }
                ].map(mot => html`
                  <label class="selection-card ${estMotor === mot.id ? 'selected' : ''}" style="min-height: auto; padding: 16px 12px; display: flex; flex-direction: column; align-items: center; justify-content: center;" onClick=${() => setEstMotor(mot.id)}>
                    <input type="radio" name="estMotor" value=${mot.id} checked=${estMotor === mot.id} class="hidden-input" />
                    <div class="card-text-wrapper" style="text-align: center;">
                      <span class="card-title" style="font-size: 14px; font-weight: 600; display: block;">${mot.title}</span>
                      <span class="card-desc" style="font-size: 12px; color: var(--sl-text-secondary); display: block; margin-top: 4px;">${mot.sub}</span>
                    </div>
                  </label>
                `);
              })()}
            </div>
          </div>
        </div>
      </div>

      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${handleRetake}>
          ${t('Ta nytt bilde', 'Neues Foto', 'Retake')}
        </button>
        <button class="button--brand" onClick=${handleConfirmWindow}>
          ${t('Bekreft', 'Bestätigen', 'Confirm')}
        </button>
      </div>
    </div>
  `;

  const renderDetected = () => html`
    <div>
      <div class="measure-header" style="text-align: left;">
        <div style="width: 48px; height: 48px; background-color: #D1FAE5; color: #059669; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 0 16px;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
        <h1 style="margin: 0; font-size: 24px; font-weight: 700;">
          ${t(
            `Fant ${detectedWindows.length} vinduer!`,
            `${detectedWindows.length} Fenster erkannt!`,
            `Detected ${detectedWindows.length} windows!`
          )}
        </h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 8px; line-height: 1.5; font-size: 14.5px;">
          ${t(
            `Vi har analysert fasaden og funnet ${detectedWindows.length} vinduer. La oss gå gjennom dem ett og ett for å gi et nøyaktig prisoverslag.`,
            `Wir haben die Fassade analysiert und ${detectedWindows.length} Fenster gefunden. Lass uns diese nacheinander durchgehen, um einen genauen Kostenvoranschlag zu erstellen.`,
            `We analyzed the facade and found ${detectedWindows.length} windows. Let's review them one by one to generate an accurate price estimate.`
          )}
        </p>
      </div>

      <div class="measure-content" style="text-align: left;">
        <!-- Uploaded image preview -->
        <div style="position: relative; width: 100%; max-width: 320px; height: 220px; border-radius: 12px; overflow: hidden; margin: 0; border: 1px solid rgba(0,0,0,0.1); background-color: #FAFAFA; box-shadow: 0 4px 12px rgba(0,0,0,0.05);">
          <img src=${draftWindow ? draftWindow.image : ''} style="width: 100%; height: 100%; object-fit: cover;" />
          <div style="position: absolute; bottom: 12px; right: 12px; background: rgba(0, 0, 0, 0.7); color: #fff; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600;">
            ${t('AI-skanning fullført', 'KI-Scan abgeschlossen', 'AI Scan Completed')}
          </div>
        </div>
      </div>

      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand-secondary" onClick=${handleRetake}>
          ${t('Ta nytt bilde', 'Neues Foto', 'Retake')}
        </button>
        <button class="button--brand" onClick=${() => setStep('confirm')}>
          ${t('Start bekreftelse', 'Bestätigung starten', 'Start confirmation')}
        </button>
      </div>
    </div>
  `;

  const renderOverview = () => html`
    <div>
      <div class="measure-header">
        <h1 style="margin: 0;">${t('Dine vinduer', 'Deine Fenster', 'Your Windows')}</h1>
        <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 6px; line-height: 1.5;">
          ${t('Her er vinduene dine. Du kan legge til flere før vi regner ut prisoverslaget.', 'Hier sind deine Fenster. Du kannst weitere hinzufügen, bevor wir den Kostenvoranschlag erstellen.', 'Here are your windows. You can add more before generating the price estimate.')}
        </p>
      </div>

      <div class="measure-content">
        <div class="windows-list" style="margin-bottom: 16px;">
          ${windows.map((win, i) => html`
            <div class="window-card" key=${win.id}>
              <div class="window-card-info">
                <div class="window-card-image">
                  <img src=${win.image} alt="Vindu" />
                </div>
                <div class="window-card-details" style="display: flex; flex-direction: column; gap: 4px;">
                  <h3 style="margin: 0; font-size: 16px; font-weight: 600;">${win.name}</h3>
                  <p style="margin: 0; font-size: 14px; color: var(--sl-text-secondary);">${win.widthWidth} × ${win.heightHeight} mm</p>
                  <p style="margin: 0; font-size: 13.5px; color: var(--sl-text-secondary);">
                    ${win.suggestedFrameColor === 'white' ? t('Hvit', 'Weiß', 'White') : (win.suggestedFrameColor === 'black' ? t('Sort', 'Schwarz', 'Black') : t('Antrasitt', 'Anthrazit', 'Anthracite'))} • ${win.suggestedMotor === 'solar' ? t('Solcelle', 'Solar', 'Solar') : t('Standard kablet', 'Kabelgebunden', 'Wired')}
                  </p>
                  ${(() => {
                    const minW = 650;
                    const minH = 200;
                    const maxW = 5000;
                    const maxH = 3000;
                    const w = win.widthWidth;
                    const h = win.heightHeight;
                    if (w < minW || w > maxW || h < minH || h > maxH) {
                      return html`
                        <p style="margin: 4px 0 0; font-size: 13px; color: #DC2626; font-weight: 500;">
                          ⚠️ ${t(
                            `Utenfor grenser (min. ${minW}x${minH} mm)`,
                            `Außerhalb der Grenzen (min. ${minW}x${minH} mm)`,
                            `Out of bounds (min. ${minW}x${minH} mm)`
                          )}
                        </p>
                      `;
                    }
                    return null;
                  })()}
                </div>
              </div>
              <div class="window-card-actions">
                <button class="text-btn" onClick=${() => duplicateWindow(win)}><${CopyIcon} /> ${t('Dupliser', 'Duplizieren', 'Duplicate')}</button>
                <button class="text-btn text-btn-danger" onClick=${() => deleteWindow(win.id)}><${TrashIcon} /> ${t('Fjern', 'Löschen', 'Remove')}</button>
              </div>
            </div>
          `)}
        </div>

        <button class="dashed-add-btn" style="width: 100%; margin-bottom: 16px;" onClick=${startNewWindow}>
          + ${t('Legg til et vindu til', 'Weiteres Fenster hinzufügen', 'Add Another Window')}
        </button>
      </div>

      <div class="measure-footer measure-footer-buttons">
        <button class="button--brand" style="width: 100%;" onClick=${() => setStep('calculating')}>
          ${t('Vis prisoverslag', 'Preisschätzung anzeigen', 'Show price estimate')}
        </button>
      </div>
    </div>
  `;

  const renderCalculating = () => {
    const steps = [
      {
        no: 'Beregner priser...',
        de: 'Preise werden berechnet...',
        en: 'Calculating prices...'
      },
      {
        no: 'Sjekker motor- og profilvalg...',
        de: 'Antriebs- und Rahmenoptionen werden geprüft...',
        en: 'Verifying motor and frame options...'
      },
      {
        no: 'Optimaliserer rabatter og kampanjer...',
        de: 'Rabatte und Aktionen werden angewendet...',
        en: 'Optimizing discounts and campaigns...'
      },
      {
        no: 'Genererer ditt tilbud...',
        de: 'Angebot wird erstellt...',
        en: 'Generating your quote...'
      }
    ];

    const currentPercentage = Math.min(((calcStage + 1) / 4) * 100, 100);

    return html`
      <div class="measure-content" style="display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 48px 24px; min-height: 420px;">
        
        <h2 style="font-size: 22px; font-weight: 800; color: var(--sl-text-primary); margin: 0 0 8px;">
          ${t('Beregner pris...', 'Preis wird kalkuliert...', 'Calculating estimate...')}
        </h2>
        
        <p style="font-size: 14.5px; color: var(--sl-text-secondary); margin-bottom: 32px; max-width: 320px; line-height: 1.5;">
          ${t(
            'Vennligst vent mens vi kalkulerer prisen basert på dine valg.',
            'Bitte warte einen Moment, während wir den Preis basierend auf deinen Angaben berechnen.',
            'Please wait a moment while we calculate the final price based on your choices.'
          )}
        </p>

        <div style="width: 100%; max-width: 300px; height: 6px; background-color: #E5E7EB; border-radius: 9999px; overflow: hidden; margin-bottom: 32px; position: relative;">
          <div style="height: 100%; width: ${currentPercentage}%; background: linear-gradient(90deg, #5B5BF9, #4F46E5); border-radius: 9999px; transition: width 0.3s ease-in-out;"></div>
        </div>

        <div style="width: 100%; max-width: 320px; display: flex; flex-direction: column; gap: 14px; text-align: left; background: #FAFAFA; border: 1px solid #E5E7EB; border-radius: 12px; padding: 20px;">
          ${steps.map((s, idx) => {
            const isCompleted = calcStage > idx;
            const isActive = calcStage === idx;
            const isPending = calcStage < idx;
            
            return html`
              <div style="display: flex; align-items: center; gap: 12px; opacity: ${isPending ? 0.4 : 1}; transition: opacity 0.3s ease;">
                <div style="width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 12px;
                  background-color: ${isCompleted ? '#D1FAE5' : (isActive ? '#EEF2FF' : '#F3F4F6')};
                  color: ${isCompleted ? '#059669' : (isActive ? '#4F46E5' : '#9CA3AF')};
                  border: 1px solid ${isCompleted ? '#A7F3D0' : (isActive ? '#C7D2FE' : '#E5E7EB')};">
                  ${isCompleted ? html`
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  ` : (isActive ? html`
                    <span class="spinner" style="width: 10px; height: 10px; border: 1.5px solid #4F46E5; border-top-color: transparent; border-radius: 50%; display: inline-block; animation: spin 0.8s infinite linear;"></span>
                  ` : '')}
                </div>
                <span style="font-size: 13.5px; font-weight: ${isActive ? '600' : '500'}; color: ${isActive ? '#4F46E5' : 'var(--sl-text-primary)'}; transition: color 0.3s ease;">
                  ${t(s.no, s.de, s.en)}
                </span>
              </div>
            `;
          })}
        </div>

      </div>
    `;
  };

  const renderPriceEstimate = () => {
    const symbol = window.ScreenluxData?.config?.currencySymbol || '€';
    const totalCent = windows.reduce((sum, win) => {
      return sum + getScreenEstimatePrice(win.widthWidth, win.heightHeight, win.suggestedMotor, win.suggestedFrameColor);
    }, 0);
    const discountedTotalCent = totalCent * 0.70;

    return html`
      <div>
        <div class="measure-header" style="align-items: center; text-align: center; padding-bottom: 10px; width: 100%;">
          <h1 style="margin: 0; font-size: 26px; font-weight: 800; text-align: center;">
            ${t('Ditt uforpliktende prisoverslag 🎉', 'Dein unverbindliches Angebot 🎉', 'Your Price Estimate 🎉')}
          </h1>
          <p style="color: rgba(var(--color-foreground), 0.7); margin-top: 8px; line-height: 1.5; font-size: 14.5px; text-align: center;">
            ${t(
              'Her er ditt estimerte prisoverslag. Du kan gå videre til produktkonfiguratoren for å finjustere valgene dine.',
              'Hier ist dein geschätztes Angebot. Du kannst direkt zum Konfigurator weitergehen, um deine Auswahl anzupassen.',
              'Here is your estimated pricing. You can continue to the configurator to customize and finalize details.'
            )}
          </p>
        </div>

        <div class="measure-content">
          <div style="display: flex; flex-direction: column; gap: 8px; background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
            ${windows.map((win, index) => {
              const price = getScreenEstimatePrice(win.widthWidth, win.heightHeight, win.suggestedMotor, win.suggestedFrameColor);
              const isInvalid = price === 0;
              return html`
                <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: ${index === windows.length - 1 ? 'none' : '1px solid #F3F4F6'};">
                  <div>
                    <div style="font-weight: 600; font-size: 15px; color: var(--sl-text-primary);">${win.name}</div>
                    <div style="font-size: 13px; color: var(--sl-text-secondary); margin-top: 2px;">
                      ${win.widthWidth} × ${win.heightHeight} mm • ${win.suggestedFrameColor === 'white' ? t('Hvit', 'Weiß', 'White') : (win.suggestedFrameColor === 'black' ? t('Sort', 'Schwarz', 'Black') : t('Antrasitt', 'Anthrazit', 'Anthracite'))} • ${win.suggestedMotor === 'solar' ? t('Solcelle', 'Solar', 'Solar') : t('Kabelgebunden', 'Kabelgebunden', 'Wired')}
                    </div>
                    ${isInvalid && html`
                      <div style="font-size: 12px; color: #DC2626; font-weight: 500; margin-top: 4px;">
                        ⚠️ ${t(
                          'For lite/stort (min. 760x200 mm)',
                          'Zu klein/groß (min. 760x200 mm)',
                          'Too small/large (min. 760x200 mm)'
                        )}
                      </div>
                    `}
                  </div>
                  <div style="font-weight: 700; font-size: 16px; color: ${isInvalid ? '#DC2626' : 'var(--sl-text-primary)'};">
                    ${isInvalid ? '—' : `${(price / 100).toFixed(0)} ${symbol}`}
                  </div>
                </div>
              `;
            })}
          </div>

          <div style="border-top: 1px solid var(--sl-border-default, #E5E7EB); padding-top: 20px; margin-top: 16px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
            <span style="font-weight: 600; font-size: 18px; color: var(--sl-text-primary);">${t('Totalpris:', 'Gesamtsumme:', 'Total estimate:')}</span>
            <div style="text-align: right;">
              ${totalCent > 0 ? html`
                <span style="font-size: 15px; color: var(--sl-text-secondary); text-decoration: line-through; margin-right: 8px;">
                  ${(totalCent / 100).toFixed(0)} ${symbol}
                </span>
                <span style="font-weight: 700; font-size: 20px; color: #10B981;">
                  ${(discountedTotalCent / 100).toFixed(0)} ${symbol}
                </span>
              ` : '—'}
            </div>
          </div>

          ${totalCent > 0 && html`
            <div style="text-align: right; font-size: 11px; color: var(--sl-text-secondary); margin-top: 6px;">
              * ${t(
                'Gjelder dersom du har meldt deg på kampanjen',
                'Gilt, wenn du dich für die Aktion angemeldet hast',
                'Applies if you signed up for the campaign'
              )}
            </div>
          `}


          <p style="font-size: 12px; color: var(--sl-text-secondary); text-align: center; margin-top: 16px; line-height: 1.5; padding: 0 12px;">
            ${t(
              '*Prisene er estimater inkludert mva. Frakt og eventuelle monteringskostnader kan tilkomme. Du kan legge til tilbehør og velge andre stoffer i neste steg.',
              '*Preise sind unverbindliche Schätzungen inkl. MwSt., zzgl. eventueller Versand- oder Montagekosten. Du kannst Stoffe, Steuerungen und Zubehör im nächsten Schritt anpassen.',
              '*Prices are estimated incl. VAT, excl. shipping/installation costs. You can change fabrics, frame colors, and add-ons in the next step.'
            )}
          </p>
        </div>

        <div class="measure-footer measure-footer-buttons">
          <button class="button--brand-secondary" onClick=${() => setStep('overview')}>
            ${t('Tilbake', 'Zurück', 'Back')}
          </button>
          <button class="button--brand" onClick=${handleGoToConfigurator}>
            ${t('Fortsett til konfigurator', 'Zur Konfiguration', 'Go to configurator')}
          </button>
        </div>
      </div>
    `;
  };

  const handleGoToConfigurator = () => {
    // Write motor option to all screens
    const updatedWindows = windows.map(w => ({
      ...w,
      suggestedMotor: w.suggestedMotor || 'solar'
    }));

    localStorage.setItem('screenlux_measurements', JSON.stringify(updatedWindows));
    localStorage.setItem('screenlux_transfer_measurements', 'true');
    localStorage.setItem('screenlux_estimate_transfer', 'true');

    window.location.href = '/products/zip-screen-configurator';
  };

  const getStepRender = () => {
    switch (step) {
      case 'welcome': return renderWelcome();
      case 'upload': return renderUpload();
      case 'processing': return renderProcessing();
      case 'detected': return renderDetected();
      case 'confirm': return renderConfirm();
      case 'overview': return renderOverview();
      case 'calculating': return renderCalculating();
      case 'estimate': return renderPriceEstimate();
      default: return renderWelcome();
    }
  };

  return html`
    <div class="measure-container">
      ${getStepRender()}
    </div>
  `;
};

// Render to DOM
const rootEl = document.getElementById('price-estimator-root');
if (rootEl) {
  render(html`<${PriceEstimatorApp} />`, rootEl);
}
